import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';
import _ from 'lodash';

import {
  GiftOrder,
  GiftOrderCancel,
  GiftOrderCourtesyUpdate,
  GiftOrderCreateDto,
  GiftOrderPartialCancel,
  GiftOrderPartialCancelResponse,
  GiftOrderUpdate,
  GiftStoreRole,
} from './giftOrder.interface';

const validRolesCreateGiftOrder = [LowerRole.StoreCustomer, LowerRole.StoreGuest];

/**
 * GiftOrder 생성
 */
export const createGiftOrder = async (
  giftOrderDto: GiftOrderCreateDto,
  storeRole: GiftStoreRole,
) => {
  if (!_.includes(validRolesCreateGiftOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    const postGiftOrderResponse = await api_.post<GiftOrder>(
      `/${storeRole}/gift-order/create`,
      giftOrderDto,
    );
    return postGiftOrderResponse;
  }
};

/**
 * GiftOrder 결제 검증
 */
export const verifyGiftOrderById = async (id: string, storeRole: GiftStoreRole) => {
  if (!_.includes(validRolesCreateGiftOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    const { data: isVerify } = await api_.get<boolean>(
      `${storeRole}/gift-order/success/${id}`,
    );
    return isVerify;
  }
};

/**
 * GiftOrder 삭제 (결제 취소/실패 시)
 */
export const deleteGiftOrderById = async (id: string, storeRole: GiftStoreRole) => {
  if (!_.includes(validRolesCreateGiftOrder, storeRole)) {
    throw new Error(`${storeRole} role can't use this api`);
  } else {
    return await api_.delete<GiftOrder>(`${storeRole}/gift-order/delete/${id}`);
  }
};

/**
 * GiftOrder 수정
 */
export const updateGiftOrder = async (
  giftOrderUpdate: GiftOrderUpdate,
  role: LowerRole,
) => {
  if (role === LowerRole.Admin) {
    const { data } = await api_.patch(
      `/${role}/gift-order/${giftOrderUpdate._id}`,
      giftOrderUpdate,
    );
    return { data };
  } else {
    const { data } = await api_.patch(
      `/${role}/gift-order/patch-detail/${giftOrderUpdate._id}`,
      giftOrderUpdate,
    );
    return { data };
  }
};

/**
 * GiftOrder 취소
 */
export const cancelGiftOrder = async (giftOrder: GiftOrderCancel, role: LowerRole) => {
  const { data } = await api_.patch(
    `${role}/gift-order/cancel/${giftOrder._id}`,
    giftOrder,
  );
  return { data };
};

/**
 * GiftOrder 부분취소 (Admin 전용)
 */
export const partialCancelGiftOrder = async (dto: GiftOrderPartialCancel) => {
  const { data } = await api_.patch<GiftOrderPartialCancelResponse>(
    `${LowerRole.Admin}/gift-order/partial-cancel/${dto._id}`,
    {
      cancelReason: dto.cancelReason,
      couponIds: dto.couponIds,
      deliveryIds: dto.deliveryIds,
    },
  );
  return { data };
};

/**
 * 가상계좌 알림 발송
 */
export const sendGiftVBankAlimUser = async (id: string, vBank: string) => {
  const result = await api_.post(`public/gift-order/send-v-bank-message/${id}`, {
    vBank,
  });
  return result;
};

/**
 * GiftOrder 목록 조회 (by bugoId)
 */
export const getGiftOrderListByBugo = async (bugoId: string) => {
  const { data } = await api_.get<GiftOrder[]>(`public/gift-order/by-bugo/${bugoId}`);
  return data;
};

/**
 * GiftOrder 답례글 업데이트
 */
export const updateGiftOrderCourtesy = async (
  orderId: string,
  dto: GiftOrderCourtesyUpdate,
  role: GiftStoreRole,
) => {
  const { data } = await api_.patch<GiftOrder>(`/${role}/gift-order/${orderId}`, dto);
  return data;
};
